import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  id?: string;
}

/**
 * Single-select searchable combobox. Same visual language as MultiSelect,
 * but commits a single value and closes on pick. Supports clear ✕ when
 * `clearable` (default true) and a value is present.
 */
export const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Выбери…",
      emptyText = "Ничего не найдено",
      searchPlaceholder = "Поиск…",
      disabled,
      clearable = true,
      className,
      id,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [activeIndex, setActiveIndex] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const listRef = React.useRef<HTMLDivElement | null>(null);

    const selected = React.useMemo(
      () => (value ? options.find((o) => o.value === value) ?? null : null),
      [options, value],
    );

    const filtered = React.useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return options;
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q) ||
          (o.hint?.toLowerCase().includes(q) ?? false),
      );
    }, [options, query]);

    React.useEffect(() => {
      // When opening, seed active index to current selection if visible
      if (open) {
        const idx = filtered.findIndex((o) => o.value === value);
        setActiveIndex(idx >= 0 ? idx : 0);
        const t = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(t);
      } else {
        setQuery("");
      }
    }, [open, value, filtered]);

    React.useEffect(() => {
      if (!open) return;
      function onDown(e: MouseEvent) {
        if (!containerRef.current) return;
        if (!containerRef.current.contains(e.target as Node)) setOpen(false);
      }
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    function pick(v: string) {
      onChange(v);
      setOpen(false);
    }

    function clear(e: React.MouseEvent) {
      e.stopPropagation();
      onChange(null);
    }

    function onKeyDown(e: React.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        scrollIntoView(listRef.current, activeIndex + 1);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        scrollIntoView(listRef.current, activeIndex - 1);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const opt = filtered[activeIndex];
        if (opt) pick(opt.value);
      }
    }

    const showClear = clearable && !!selected && !disabled;

    return (
      <div ref={mergeRefs(ref, containerRef)} className={cn("relative", className)}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 px-3 text-left text-sm",
            "shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors",
            "hover:border-zinc-700",
            open && "border-zinc-600 ring-1 ring-zinc-600",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2 truncate text-zinc-100">
              <span className="truncate">{selected.label}</span>
              {selected.hint && (
                <span className="shrink-0 font-mono text-[11px] text-zinc-500">
                  {selected.hint}
                </span>
              )}
            </span>
          ) : (
            <span className="text-zinc-500">{placeholder}</span>
          )}
          <span className="flex items-center gap-1 text-zinc-500">
            {showClear && (
              <span
                role="button"
                tabIndex={-1}
                onClick={clear}
                className="grid h-5 w-5 place-items-center rounded-sm hover:bg-zinc-800 hover:text-zinc-200"
                aria-label="Очистить"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronDown className="h-4 w-4" />
          </span>
        </button>

        {open && (
          <div
            className={cn(
              "absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-zinc-800 bg-[#121215]",
              "shadow-[0_12px_32px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.04)]",
            )}
          >
            <div className="border-b border-zinc-800/80 p-2">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                className={cn(
                  "h-8 w-full rounded-sm border-0 bg-transparent px-1 text-sm text-zinc-100",
                  "placeholder:text-zinc-500 focus:outline-none",
                )}
              />
            </div>
            <div
              ref={listRef}
              className="max-h-64 overflow-y-auto p-1"
              role="listbox"
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-zinc-500">
                  {emptyText}
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const isSelected = opt.value === value;
                  const isActive = i === activeIndex;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => pick(opt.value)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                        isActive ? "bg-zinc-800/80 text-zinc-50" : "text-zinc-200",
                      )}
                    >
                      <span className="grid h-4 w-4 place-items-center text-[#ff5c35]">
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1 truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="font-mono text-[11px] text-zinc-500">
                          {opt.hint}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);
Combobox.displayName = "Combobox";

function scrollIntoView(list: HTMLDivElement | null, idx: number) {
  if (!list) return;
  const el = list.children[idx] as HTMLElement | undefined;
  if (!el) return;
  const top = el.offsetTop;
  const bottom = top + el.offsetHeight;
  if (top < list.scrollTop) list.scrollTop = top;
  else if (bottom > list.scrollTop + list.clientHeight)
    list.scrollTop = bottom - list.clientHeight;
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (node) => {
    for (const r of refs) {
      if (!r) continue;
      if (typeof r === "function") r(node);
      else (r as React.MutableRefObject<T | null>).current = node;
    }
  };
}
